import { ref, computed, inject } from 'vue';
import type { ComputedRef } from 'vue';
import { UserSection } from '../models/UserSection';
import { UserStep } from 'modules/Step/models/UserStep';
import { useRepo } from 'pinia-orm';
import { useAssignments } from 'shared/composables/useAssignments';
import type {
  UserSectionProgress,
  UserSectionStepProgress,
} from 'src/types/course/section';
import type { AxiosError } from 'axios';
import {
  useStrapiGet,
  useStrapiPost,
  useStrapiPut,
  getJwtFromLocalStorage,
  notifyError,
} from 'shared/composables/useStrapiApi';
import { toKebabCase } from 'utils/string';
import type { StackType } from 'src/types/course/stack';
import type { UserStepProgress } from 'src/types/course/step';
import { usePremiumAccessGuard } from 'src/modules/Course/composables/usePremiumAccessGuard';
import type { UserRole } from 'src/types/User';
import {
  calculateSectionAccumulatedTime,
  calculateSectionAverageScore,
} from '../utilities/sectionProgressMetrics';
import {
  countFinishedSectionSteps,
  hasSectionStarted,
} from '../utilities/sectionProgressState';
import { sectionProgressWriteQueue } from '../composables/sectionProgressWriteQueue';
import {
  compareByListCriteria,
  matchesListCriteria,
  toListQueryParams,
} from 'shared/composables/useListFilters/useListFilters';
import type {
  FilterableListItem,
  ListFilterCriteria,
} from 'shared/composables/useListFilters/useListFilters';

export type Filter =
  | { key: 'is_assigned'; value: boolean }
  | { key: 'started_only'; value: boolean }
  | undefined;

const {
  fetchUserSections,
  isLastSection,
  isLastFilteredSection,
  sectionTotal,
} = useAssignments();
const userSectionRepo = useRepo(UserSection);

/** Per-request axios timeout: a hung PUT must not hold the section's write queue. */
const SECTION_PUT_TIMEOUT_MS = 15_000;

/** Adapter: a user-section row seen through the shared list-filter shape. */
export function toFilterableItem(userSection: UserSection): FilterableListItem {
  return {
    name: userSection.section?.section_name ?? '',
    startDate: userSection.start_date,
    completionDate: userSection.completion_date,
    score: userSection.score,
    timeAccumulator: userSection.time_accumulator,
    progressCount: userSection.progress?.steps?.length ?? 0,
  };
}

function parseProgress(value: unknown): Partial<UserStepProgress> {
  if (typeof value === 'string') {
    try {
      return (JSON.parse(value) ?? {}) as Partial<UserStepProgress>;
    } catch {
      return {};
    }
  }
  return value && typeof value === 'object' ? value : {};
}

/**
 * A server copy of a user step that arrives after the step was answered
 * locally (BUG-27). The server copy wins only when it already holds every
 * local result with at least as many tries; otherwise the local progress and
 * its outcome fields are kept, and any result only the server knows is added,
 * so no try on either side is ever dropped.
 */
export function reconcileUserStep(
  local: UserStep,
  incoming: Record<string, unknown>,
): Record<string, unknown> {
  const localProgress = parseProgress(local.progress);
  const serverProgress = parseProgress(incoming.progress);
  const localResults = localProgress.stack?.results ?? {};
  const serverResults = serverProgress.stack?.results ?? {};

  const serverCoversLocal = Object.entries(localResults).every(
    ([key, result]) =>
      (serverResults[key]?.tries?.length ?? -1) >= (result?.tries?.length ?? 0),
  );
  if (serverCoversLocal) return incoming;

  const results = { ...serverResults };
  for (const [key, result] of Object.entries(localResults)) {
    const server = serverResults[key];
    if (!server || (server.tries?.length ?? 0) <= (result?.tries?.length ?? 0))
      results[key] = result;
  }

  return {
    ...incoming,
    start_date: local.start_date ?? incoming.start_date ?? null,
    completion_date: local.completion_date,
    score: local.score,
    time_accumulator: local.time_accumulator,
    progress: {
      ...serverProgress,
      ...localProgress,
      stack: {
        ...serverProgress.stack,
        ...localProgress.stack,
        results,
      },
    },
  };
}

export const useUserSectionData = ({
  userSectionDocId,
  filter,
  criteria,
}: {
  userSectionDocId?: string;
  filter?: Filter;
  criteria?: ComputedRef<ListFilterCriteria>;
} = {}) => {
  const currentUserRole =
    inject<ComputedRef<UserRole | undefined>>('currentUserRole');
  const isSchoolStudent = computed(() => currentUserRole?.value === 'student');

  const isLoading = ref(false);
  const { handlePremiumAccessDenied } = usePremiumAccessGuard();
  // const currentStepId = ref('');

  const userSectionByCurrentId = computed(() =>
    userSectionRepo
      .withAllRecursive()
      .where('documentId', userSectionDocId)
      .first(),
  );

  // if no user section is found, fetch it
  if (userSectionDocId && !userSectionByCurrentId.value) {
    void getUserSections(userSectionDocId);
  }

  // Sections before list criteria are applied — the "M" of the bar's "N / M".
  const baseUserSections = computed(() => {
    const allUserSections = userSectionRepo
      .withAllRecursive()
      .where('user_course_id', -1)
      .get();

    if (filter?.key && filter.value !== undefined) {
      return allUserSections.filter((userSection) => {
        if (filter.key === 'is_assigned') {
          return userSection.is_assigned === filter.value;
        }

        if (filter.key === 'started_only') {
          return hasSectionStarted(userSection);
        }

        return false;
      });
    }

    return allUserSections.filter((userSection) => {
      if (userSection.is_assigned !== false) return true;
      if (!isSchoolStudent.value) return false;
      return hasSectionStarted(userSection);
    });
  });

  // The list criteria are mirrored over the cache (same definitions the
  // backend applies — see useListFilters) so the display reacts instantly and
  // cached records from other pages can never leak into a filtered view.
  const userSections = computed(() => {
    if (!criteria?.value) return baseUserSections.value;
    const current = criteria.value;
    return baseUserSections.value
      .filter((userSection) =>
        matchesListCriteria(toFilterableItem(userSection), current),
      )
      .sort((a, b) =>
        compareByListCriteria(
          toFilterableItem(a),
          toFilterableItem(b),
          current.sortBy,
        ),
      );
  });

  const currentUserSection = computed(() => {
    return userSectionRepo
      .withAllRecursive()
      .where('documentId', userSectionDocId);
  });

  async function getUserSections(userSectionDocId?: string) {
    isLoading.value = true;
    if (userSectionDocId) {
      await fetchUserSections(userSectionDocId);
    } else if (filter?.key) {
      // fetchAll paths hold the complete set client-side; the mirrored
      // criteria above are sufficient, so no server criteria are sent.
      await fetchUserSections({ fetchAll: true });
    } else if (criteria?.value && criteria.value.statuses.length === 0) {
      // Empty status selection shows nothing — no point asking the server.
    } else {
      await fetchUserSections({
        criteria: criteria?.value ? toListQueryParams(criteria.value) : {},
      });
    }
    isLoading.value = false;
  }

  const sectionByCurrentId = computed(() => {
    return userSectionByCurrentId.value?.section;
  });

  const currentSection = computed(() => {
    return userSectionByCurrentId.value?.section;
  });

  const sectionDocId = computed(() => {
    return currentSection.value?.documentId;
  });

  function getUpdateSectionData(userSectionData: Partial<UserSection>) {
    const { progress, score, completion_date, time_accumulator, start_date } =
      userSectionData;
    const sectionData = {};
    if (progress !== undefined) Object.assign(sectionData, { progress });
    if (score !== undefined)
      Object.assign(sectionData, { score: Math.round(score) });
    if (completion_date !== undefined)
      Object.assign(sectionData, { completion_date });
    if (time_accumulator !== undefined)
      Object.assign(sectionData, { time_accumulator });
    // Same rule as getUpdateCourseData: explicit value wins, otherwise only
    // stamp when the record is loaded, so a flushed repo cannot overwrite a
    // real start date with `now`.
    if (start_date !== undefined) {
      Object.assign(sectionData, { start_date });
    } else if (userSectionByCurrentId.value) {
      Object.assign(sectionData, {
        start_date:
          userSectionByCurrentId.value.start_date ?? new Date().toISOString(),
      });
    }
    return sectionData;
  }

  function updateUserSectionRepo(userSectionData: Partial<UserSection>) {
    if (!userSectionRepo?.value || !userSectionDocId) return;
    const sectionData = getUpdateSectionData(userSectionData);
    currentUserSection.value.update(sectionData);
  }

  async function updateUserSectionStrapi(
    userSectionData: Partial<UserSection>,
  ) {
    if (!userSectionRepo?.value || !userSectionDocId) return;
    if (!getJwtFromLocalStorage()) return;
    const sectionData = getUpdateSectionData(userSectionData);
    const docId = userSectionDocId;

    // Every section PUT goes through the section's serial queue: one in
    // flight, later writes coalesced, so no write overtakes an earlier one.
    // Resolves once the request carrying this write has settled; failures
    // are reported (axios error toast, premium redirect), never thrown.
    await sectionProgressWriteQueue(docId).enqueue(
      sectionData,
      async (data) => {
        const { execute } = useStrapiPut(`user-sections/${docId}/progress`, {
          data,
        });
        try {
          await execute({ timeout: SECTION_PUT_TIMEOUT_MS });
        } catch (error) {
          if (handlePremiumAccessDenied(error)) return;
          console.error('updateUserSectionStrapi:', error);
        }
      },
    );
  }

  // Keeps the user step in strapi
  async function removeUserStepProgress(userStepIds?: number[]) {
    if (!currentSectionProgress.value) return;
    const { steps, ...rest } = currentSectionProgress.value;

    const newSteps =
      userStepIds && Array.isArray(userStepIds)
        ? steps?.filter(
            (step: UserSectionStepProgress) =>
              !userStepIds.includes(step.user_step_id),
          )
        : [];

    const resetData = {
      progress: {
        ...rest,
        steps: newSteps,
      },
    };

    updateUserSectionRepo(resetData);
    await updateUserSectionStrapi(resetData);
  }

  // deletes user steps from strapi and update the section progress
  // if userStepIds is passed, it will delete the steps that are not in the array
  // if userStepIds is not passed, it will delete all the steps
  async function resetUserSectionProgress(userStepIds?: number[]) {
    if (!currentSectionProgress.value || !sectionDocId.value) return;

    const userStepsArray =
      userStepIds && Array.isArray(userStepIds) ? userStepIds : [];

    // Queued progress PUTs land first, so none can undo the reset.
    if (userSectionDocId)
      await sectionProgressWriteQueue(userSectionDocId).whenIdle();

    const { execute, data } = useStrapiPost(
      `user-sections/${userSectionByCurrentId.value?.documentId}/reset`,
      {
        userSteps: userStepsArray,
      },
    );

    try {
      await execute();
      if (data.value) {
        updateUserSectionRepo({
          progress:
            userStepsArray.length === 0 ? {} : (data.value?.progress ?? {}),
        });
      }
    } catch (error) {
      console.error('resetUserSectionProgress:', error);
    }
  }

  /**
   * Loads a user-step into the store. 'missing' = the server has no such
   * user-step for this user (deleted, e.g. by a reset, while the section
   * progress or the URL still points at it): see replaceMissingUserStep.
   */
  async function getUserStepData(
    userStepId: number,
  ): Promise<UserStep | 'missing' | undefined> {
    const userStepRepo = useRepo(UserStep);
    const userStep = userStepRepo.find(userStepId);
    // Skip fetch if exists in pinia store
    if (userStep) return;

    // No error toast here: a missing user-step is repaired, not reported.
    const { execute, data } = useStrapiGet(
      `user-steps/by-id/${userStepId}`,
      {},
      { suppressError: true },
    );

    try {
      const result = await execute();
      const error = result?.error as AxiosError | null | undefined;
      if (error) {
        if (error.response?.status === 404) return 'missing';
        notifyError(error);
        return undefined;
      }
      if (data.value) {
        // The row may have been loaded and answered locally while the request
        // was in flight; never let the late server copy rewind it (BUG-27).
        // withAllRecursive bypasses pinia-orm's hydrated-model cache, which a
        // plain find() may serve stale.
        const local = userStepRepo.withAllRecursive().find(userStepId);
        return userStepRepo.save(
          local
            ? reconcileUserStep(local, data.value as Record<string, unknown>)
            : (data.value as UserStep),
        );
      }
    } catch (error) {
      console.error('updateUserSectionStrapi:', error);
    }
    return undefined;
  }

  const sectionSteps = computed(() => {
    const steps = sectionByCurrentId.value?.section_step
      ?.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
      .map((s) => s.step);
    return steps ? steps : [];
  });

  const getSectionStepTermsOfTransition = (stepDocId: string) => {
    return (
      sectionByCurrentId.value?.section_step?.find(
        (s) => s.step?.documentId === stepDocId,
      )?.terms_of_transition ?? []
    );
  };

  const currentSectionProgress = computed(() => {
    return userSectionByCurrentId.value?.progress;
  });

  const currentSectionScore = computed(() => {
    return calculateSectionAverageScore(currentSectionProgress.value?.steps);
  });

  const currentSectionAccumulatedTime = computed(() => {
    return calculateSectionAccumulatedTime(currentSectionProgress.value?.steps);
  });

  function sectionStepByDocId(stepDocId: string) {
    if (!sectionSteps.value) return undefined;
    return sectionSteps.value.find((step) => step?.documentId === stepDocId);
  }

  function getSectionStep(stepDocId: string) {
    if (!currentSectionProgress.value) return undefined;
    return currentSectionProgress.value.steps?.find(
      (step) => step.step_doc_id === stepDocId,
    );
  }

  // Returns '' rather than undefined for an unknown step: both call sites feed
  // the result straight into toKebabCase(text: string), which does text.trim()
  // and so threw a TypeError whenever the doc id was not among the loaded
  // section steps. That was invisible while Step.type was implicitly `any`.
  function sectionStepComponentType(stepDocId: string): string {
    const sectionStep = sectionStepByDocId(stepDocId);
    if (!sectionStep) return '';
    // return sectionStep?.step_type?.[0]?.__component.split('.')?.[1];
    return sectionStep?.type?.toLowerCase() ?? '';
  }

  // Registers the step locally at once and queues the section PUT; the
  // returned promise settles with that PUT. Opening a step does not wait for
  // it (generateUserStep, P4-4): the queue keeps it ahead of any later section
  // write (e.g. the step:start start_date).
  function setSectionUserStepDocId(
    stepDocId: string,
    userStepId: number,
  ): Promise<void> {
    const sectionStepType = sectionStepComponentType(stepDocId);
    if (!currentSectionProgress.value) {
      const progress = {
        id: sectionByCurrentId.value?.id ?? 0,
        length: sectionSteps.value.length,
        steps: [
          {
            step_doc_id: stepDocId,
            user_step_id: userStepId,
            start_date: new Date().toISOString(),
            step_type: toKebabCase(sectionStepType) as StackType,
          },
        ],
      };

      updateUserSectionRepo({ progress });
      return updateUserSectionStrapi({ progress });
    }

    const currentSteps = currentSectionProgress.value?.steps ?? [];
    Object.assign(currentSectionProgress.value, {
      steps: [
        ...currentSteps,
        {
          step_doc_id: stepDocId,
          user_step_id: userStepId,
          step_type: toKebabCase(sectionStepType),
        },
      ],
    });
    updateUserSectionRepo({ progress: currentSectionProgress.value });
    return updateUserSectionStrapi({ progress: currentSectionProgress.value });
  }

  // Redo_Current (D-9): the step keeps its place in the section progress and
  // its original start_date; the attempt's outcome (score, completion, time —
  // the user-step itself is reset to 0 time as well) is cleared. A step that
  // was never registered is registered now, as before.
  // `original` is the entry as it was BEFORE the user-step reset: the backend
  // reset removes the step from the section progress (unawaited) and stamps a
  // new start_date, so the entry is rebuilt from the snapshot at its index.
  async function resetSectionStepProgress(
    stepDocId: string,
    userStepId: number,
    original?: { index: number; step: UserSectionStepProgress },
  ) {
    const steps = currentSectionProgress.value?.steps ?? [];
    const current = steps.find((step) => step.step_doc_id === stepDocId);
    const source = original?.step ?? current;
    if (!source) {
      await setSectionUserStepDocId(stepDocId, userStepId);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { score, completion_date, time_accumulator, ...kept } = source;
    const others = steps.filter((step) => step.step_doc_id !== stepDocId);
    const index = original
      ? Math.min(original.index, others.length)
      : steps.indexOf(current!);
    const progress = {
      ...currentSectionProgress.value,
      steps: [...others.slice(0, index), kept, ...others.slice(index)],
    } as UserSectionProgress;

    updateUserSectionRepo({ progress });
    await updateUserSectionStrapi({ progress });
  }

  /**
   * The step's user-step no longer exists (getUserStepData → 'missing'): a
   * new one takes its place. The step's section-progress entry keeps its
   * position, start_date, score and completion (the student loses nothing);
   * only its user_step_id changes, and duplicate entries of the step collapse
   * into it. A step with no entry is registered as usual. Returns the new id.
   */
  async function replaceMissingUserStep(stepDocId: string) {
    const newUserStepId = await createUserStep(
      userSectionByCurrentId.value?.id ?? -1,
      userSectionByCurrentId.value?.user_course_id ?? -1,
      stepDocId,
    );
    if (!newUserStepId) return undefined;

    const steps = currentSectionProgress.value?.steps ?? [];
    if (!steps.some((step) => step.step_doc_id === stepDocId)) {
      void setSectionUserStepDocId(stepDocId, newUserStepId);
      return newUserStepId;
    }

    let replaced = false;
    const progress = {
      ...currentSectionProgress.value,
      steps: steps.flatMap((step) => {
        if (step.step_doc_id !== stepDocId) return [step];
        if (replaced) return [];
        replaced = true;
        return [{ ...step, user_step_id: newUserStepId }];
      }),
    } as UserSectionProgress;
    updateUserSectionRepo({ progress });
    void updateUserSectionStrapi({ progress });
    return newUserStepId;
  }

  async function createUserStep(
    sectionId: number,
    courseId: number,
    stepDocId: string,
  ) {
    if (!stepDocId) return;
    const userStepData = {
      stepDocumentId: stepDocId,
      user_section_id: sectionId ?? -1,
      user_course_id: courseId ?? -1,
      start_date: new Date().toISOString(),
    };

    const { execute, data } = useStrapiPost('user-steps', {
      data: userStepData,
    });
    try {
      await execute();
      if (data.value?.data) {
        addUserStepToRepo(data.value.data);
        return data.value?.data?.id;
      }
    } catch (error) {
      console.error('createUserStep: Error creating user step: ', error);
    }
    return undefined;
  }

  async function generateUserStep(stepDocId: string | undefined) {
    if (!stepDocId) return undefined;

    const sectionStep = getSectionStep(stepDocId);
    let userStepId = sectionStep?.user_step_id;
    if (!sectionStep) {
      userStepId = await createUserStep(
        userSectionByCurrentId.value?.id ?? -1,
        userSectionByCurrentId.value?.user_course_id ?? -1,
        stepDocId,
      );

      // In the progress: local now, the section PUT in the background.
      if (userStepId) void setSectionUserStepDocId(stepDocId, userStepId);
    }
    return userStepId;
  }

  function addUserStepToRepo(data: Record<string, unknown>) {
    const userStepRepo = useRepo(UserStep);
    userStepRepo.save(data);
  }

  // Progress functionality
  // Number of steps
  const sectionStepsCount = computed(() =>
    sectionSteps?.value ? sectionSteps.value?.length : 0,
  );

  // Completed Steps
  const completedSteps = computed(
    () =>
      currentSectionProgress?.value?.steps?.filter(
        (step) => step.completion_date,
      ) ?? [],
  );

  const skippedSteps = computed(
    () =>
      currentSectionProgress?.value?.steps?.filter(
        (step) => step?.isSkippedFilter,
      ) ?? [],
  );

  // Completed Steps count
  const completedStepsCount = computed(() => completedSteps.value?.length ?? 0);

  // Steps that need no more work: completed or skipped.
  const finishedStepsCount = computed(() =>
    countFinishedSectionSteps(currentSectionProgress?.value?.steps),
  );

  // Completed Section
  // Same vacuous-truth guard as isCompletedCourse: a zero step count means the
  // steps are not loaded, not that they are all done.
  const isCompletedSection = computed(
    () =>
      sectionStepsCount.value > 0 &&
      finishedStepsCount.value === sectionStepsCount.value,
  );

  // The completion date is stamped on the user-section itself; the section
  // progress object never carries one, so reading it there was always false and
  // the completion branch re-ran (re-emitting and re-stamping) on every arrival
  // at the end of the section.
  const isSctionCompletionDate = computed(
    () =>
      isCompletedSection.value &&
      !!userSectionByCurrentId.value?.completion_date,
  );

  // Section ProgressPercent
  const sectionProgressPercent = computed(() =>
    sectionStepsCount.value > 0
      ? Math.round((finishedStepsCount.value / sectionStepsCount.value) * 100)
      : 0,
  );

  // Accumulated Time
  const accumulatedTime = computed(() => {
    return currentSectionProgress?.value?.steps?.reduce((acc, step) => {
      return acc + (step.time_accumulator ?? 0);
    }, 0);
  });

  // completed steps ids
  const completedStepsId = computed(() => {
    if (!currentSectionProgress.value) return [];
    const completedStepsId =
      completedSteps.value?.map((step) => step.step_doc_id) ?? [];
    return completedStepsId;
  });

  const skippedStepsId = computed(() => {
    if (!currentSectionProgress.value) return [];
    const skippedStepsId =
      skippedSteps.value?.map((step) => step.step_doc_id) ?? [];
    return skippedStepsId;
  });

  // Next Section Step (not completed and not skkiped steps)
  const nextSectionStep = computed(() => {
    if (!currentSectionProgress.value) return sectionSteps.value?.[0];
    const nextStep = sectionSteps.value?.find(
      (step) =>
        !completedStepsId.value.includes(step?.documentId ?? '') &&
        !skippedStepsId.value.includes(step?.documentId ?? ''),
    );
    return nextStep;
  });

  // Section Score
  const sectionScore = computed(() => {
    if (!currentSectionProgress.value) return undefined;
    return currentSectionScore.value;
  });

  return {
    currentSectionProgress,
    currentSectionScore,
    currentSectionAccumulatedTime,
    isLastSection,
    isLastFilteredSection,
    sectionTotal,
    isLoading,
    userSections: userSections,
    baseUserSections,
    sectionByCurrentId,
    userSectionByCurrentId,
    sectionDocId,
    sectionSteps,
    updateUserSectionRepo,
    updateUserSectionStrapi,
    getUserStepData,
    setSectionUserStepDocId,
    resetSectionStepProgress,
    getSectionStep,
    getUserSections,
    createUserStep,
    replaceMissingUserStep,
    generateUserStep,
    getSectionStepTermsOfTransition,
    currentSection,
    isSctionCompletionDate,
    // Progress
    removeUserStepProgress,
    resetUserSectionProgress,
    sectionStepsCount,
    completedStepsCount,
    isCompletedSection,
    sectionProgressPercent,
    accumulatedTime,
    completedStepsId,
    nextSectionStep,
    sectionScore,
  };
};
